// =============================================================================
// hooks/sales/useSalesSettings.ts — Queries/mutations da configuração
// comercial (F34-S06, consumindo o contrato publicado por F34-S02).
//
// Endpoints (apps/api/src/modules/sales/settings/routes.ts):
//   GET   /api/sales/settings  — configuração efetiva (fuso, janela, tentativas,
//                                 distribuição, motivos, dry-run). SEMPRE com
//                                 defaults aplicados (`isDefault: true` quando a
//                                 organização nunca salvou nada — lazy default).
//   PATCH /api/sales/settings  — atualização parcial. Campos omitidos preservam
//                                 o valor em vigor (explícito ou default).
//   GET   /api/sales/outcomes  — desfechos HABILITADOS apenas (o mesmo endpoint
//                                 usado pelas telas de registro do vendedor —
//                                 é o que GARANTE que um desfecho desabilitado
//                                 aqui some de lá; ver __tests__).
//   PUT   /api/sales/outcomes  — REPLACE completo do catálogo (exige os 9
//                                 códigos canônicos, nem mais nem menos).
//
// LIMITAÇÃO CONHECIDA (documentada, não escondida): a API não expõe hoje um
// GET que devolva o catálogo COMPLETO (habilitados + desabilitados) da
// organização — só o service (`getAllOutcomeConfigs`, sales/settings/service.ts)
// tem essa leitura, e nenhuma rota a publica. Para montar a lista de toggles
// desta tela (que precisa mostrar também os desfechos desabilitados, para
// poder reabilitá-los), este hook reconstrói o catálogo completo mesclando:
//   1. o catálogo canônico fechado (`SALES_OUTCOME_CODES`/`SALES_OUTCOME_META`,
//      shared-schemas/sales.js) — fonte da verdade de quais 9 códigos existem
//      e seus metadados-padrão;
//   2. a resposta real da API (`GET /api/sales/outcomes`) para os campos dos
//      códigos HABILITADOS — esses vêm sempre do servidor, nunca inventados.
// Um código ausente da resposta (porque está desabilitado) é preenchido com
// os metadados-padrão do catálogo + `enabled: false`. Isso é 100% correto
// quando a organização nunca customizou label/regras de um desfecho que
// desabilitou — é o caso normal. O único cenário não coberto é: organização
// customiza label/regras de um desfecho E o desabilita — ao reabrir esta
// tela, o desfecho reaparece com os metadados PADRÃO, não a customização
// anterior (perdida apenas na exibição — o PUT seguinte grava o padrão, não
// resgata o valor anterior do banco). Isso é uma lacuna do backend (falta
// `GET /api/sales/outcomes/all` ou `?includeDisabled=true`, gated por
// `sales_campaigns:manage`) fora de `files_allowed` deste slot (`apps/api/**`
// é proibido) — registrado aqui para o próximo slot de backend resolver.
// =============================================================================

import type {
  SalesOutcomeConfig,
  SalesOutcomesResponse,
  SalesSettings,
  SalesSettingsPatchBody,
} from '@elemento/shared-schemas';
import {
  SALES_OUTCOME_CODES,
  SALES_OUTCOME_META,
  SalesOutcomesResponseSchema,
  SalesSettingsSchema,
} from '@elemento/shared-schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api';

export type { SalesOutcomeConfig, SalesOutcomesResponse, SalesSettings, SalesSettingsPatchBody };

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const salesSettingsKeys = {
  settings: ['sales', 'settings'] as const,
  outcomes: ['sales', 'outcomes'] as const,
};

// ---------------------------------------------------------------------------
// Fetchers — parse defensivo runtime (mesmo padrão de useCampaigns.ts)
// ---------------------------------------------------------------------------

async function fetchSalesSettings(): Promise<SalesSettings> {
  const raw = await api.get<unknown>('/api/sales/settings');
  return SalesSettingsSchema.parse(raw);
}

async function patchSalesSettings(body: SalesSettingsPatchBody): Promise<SalesSettings> {
  const raw = await api.patch<unknown>('/api/sales/settings', body);
  return SalesSettingsSchema.parse(raw);
}

async function fetchEnabledOutcomes(): Promise<SalesOutcomesResponse> {
  const raw = await api.get<unknown>('/api/sales/outcomes');
  return SalesOutcomesResponseSchema.parse(raw);
}

async function putOutcomes(data: SalesOutcomeConfig[]): Promise<SalesOutcomesResponse> {
  const raw = await api.put<unknown>('/api/sales/outcomes', { data });
  return SalesOutcomesResponseSchema.parse(raw);
}

// ---------------------------------------------------------------------------
// useSalesSettings — leitura da configuração efetiva
// ---------------------------------------------------------------------------

export interface UseSalesSettingsOptions {
  /** Desabilita o fetch (ex: aguardando gate de permissão/flag). Default true. */
  enabled?: boolean;
}

export function useSalesSettings(opts: UseSalesSettingsOptions = {}): {
  data: SalesSettings | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: salesSettingsKeys.settings,
    queryFn: fetchSalesSettings,
    enabled: opts.enabled ?? true,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });

  return { data, isLoading, isError, error: error as Error | null, refetch: () => void refetch() };
}

// ---------------------------------------------------------------------------
// useUpdateSalesSettings — PATCH parcial com invalidate
// ---------------------------------------------------------------------------

export function useUpdateSalesSettings(): {
  update: (body: SalesSettingsPatchBody) => void;
  updateAsync: (body: SalesSettingsPatchBody) => Promise<SalesSettings>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: patchSalesSettings,
    onSuccess: (result) => {
      queryClient.setQueryData(salesSettingsKeys.settings, result);
    },
  });

  return {
    update: (body) => mutation.mutate(body),
    updateAsync: (body) => mutation.mutateAsync(body),
    isPending: mutation.isPending,
  };
}

// ---------------------------------------------------------------------------
// useSalesOutcomesCatalog — catálogo COMPLETO (habilitados + desabilitados)
// reconstruído a partir do catálogo canônico + resposta real da API. Ver
// nota de "LIMITAÇÃO CONHECIDA" no topo do arquivo.
// ---------------------------------------------------------------------------

export function buildFullOutcomeCatalog(enabledResponse: SalesOutcomesResponse): {
  items: SalesOutcomeConfig[];
  isDefault: boolean;
} {
  const byCode = new Map(enabledResponse.data.map((item) => [item.code, item]));

  const items = SALES_OUTCOME_CODES.map((code, index) => {
    const fromApi = byCode.get(code);
    if (fromApi) return fromApi;

    const meta = SALES_OUTCOME_META[code];
    return {
      code,
      enabled: false,
      label: meta.label,
      requiresNote: meta.requiresNote,
      requiresAppointment: meta.requiresAppointment,
      terminatesMember: meta.terminatesMember,
      blocksContact: meta.blocksContact,
      sortOrder: index,
    } satisfies SalesOutcomeConfig;
  });

  return { items, isDefault: enabledResponse.isDefault };
}

export function useSalesOutcomesCatalog(opts: UseSalesSettingsOptions = {}): {
  items: SalesOutcomeConfig[];
  isDefault: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: salesSettingsKeys.outcomes,
    queryFn: fetchEnabledOutcomes,
    enabled: opts.enabled ?? true,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });

  const built = data ? buildFullOutcomeCatalog(data) : { items: [], isDefault: true };

  return {
    items: built.items,
    isDefault: built.isDefault,
    isLoading,
    isError,
    refetch: () => void refetch(),
  };
}

// ---------------------------------------------------------------------------
// useEnabledOutcomes — SÓ os desfechos habilitados, já ordenados por
// `sortOrder` (F34-S05, doc 26 §7 "OutcomeSelector"/nota do slot "a lista de
// desfechos vem de GET /api/sales/outcomes, não de constante no frontend").
//
// Reusa a MESMA queryKey/queryFn de `useSalesOutcomesCatalog` acima — mesmo
// cache, zero requisição extra quando as duas telas estão montadas ao mesmo
// tempo. Diferença: esta função NÃO reconstrói o catálogo completo (não
// preenche códigos desabilitados) — é exatamente a resposta crua da API,
// que é o que o formulário de registro de contato precisa oferecer ao
// vendedor (um código desabilitado pela organização nunca deve aparecer no
// `<select>`).
// ---------------------------------------------------------------------------

export function useEnabledOutcomes(opts: UseSalesSettingsOptions = {}): {
  outcomes: SalesOutcomeConfig[];
  isLoading: boolean;
  isError: boolean;
} {
  const { data, isLoading, isError } = useQuery({
    queryKey: salesSettingsKeys.outcomes,
    queryFn: fetchEnabledOutcomes,
    enabled: opts.enabled ?? true,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });

  return { outcomes: data?.data ?? [], isLoading, isError };
}

// ---------------------------------------------------------------------------
// useReplaceSalesOutcomes — PUT (replace completo) com invalidate
// ---------------------------------------------------------------------------

export function useReplaceSalesOutcomes(): {
  replace: (data: SalesOutcomeConfig[]) => void;
  replaceAsync: (data: SalesOutcomeConfig[]) => Promise<SalesOutcomesResponse>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: putOutcomes,
    onSuccess: (result) => {
      queryClient.setQueryData(salesSettingsKeys.outcomes, result);
    },
  });

  return {
    replace: (data) => mutation.mutate(data),
    replaceAsync: (data) => mutation.mutateAsync(data),
    isPending: mutation.isPending,
  };
}
